import type {
  AppUserId,
  AuthProviderUserId,
  SubmissionId,
  VideoId,
} from "./ids";
import type { LanguageTag } from "./user";

/**
 * Status values are server-owned. A client may ask to move draft -> submitted
 * (via the submit endpoint) but may never set any other value.
 */
export const SUBMISSION_STATUSES = [
  "draft",
  "submitted",
  "in_review",
  "accepted",
  "declined",
  "withdrawn",
  "archived",
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

/** Statuses an administrator may assign. `draft` is never assignable. */
export const ADMIN_ASSIGNABLE_STATUSES = [
  "submitted",
  "in_review",
  "accepted",
  "declined",
  "archived",
] as const satisfies readonly SubmissionStatus[];
export type AdminAssignableStatus = (typeof ADMIN_ASSIGNABLE_STATUSES)[number];

/**
 * Which GrowthWise Education format the applicant is interested in.
 * Taken verbatim from section 02 of the content document.
 */
export const SERVICE_INTERESTS = [
  "lectures_and_seminars",
  "individual_consulting",
  "group_training",
  "digital_learning_and_support",
  "not_sure",
] as const;
export type ServiceInterest = (typeof SERVICE_INTERESTS)[number];

/**
 * Who the enquiry is on behalf of. Taken from section 06 of the content
 * document ("Who GrowthWise Education Serves").
 */
export const AUDIENCE_TYPES = [
  "individual",
  "educational_community",
  "organization",
  "community",
] as const;
export type AudienceType = (typeof AUDIENCE_TYPES)[number];

export const DELIVERY_PREFERENCES = ["on_site", "remote", "either"] as const;
export type DeliveryPreference = (typeof DELIVERY_PREFERENCES)[number];

/**
 * The application payload.
 *
 * OPEN QUESTION (see docs/open-questions.md, Q1): the content document
 * describes services and audiences but never specifies an intake form. These
 * fields are the minimal, configurable set the brief asks for -- name, email,
 * interest, message -- plus the audience/delivery context the document does
 * name. Nothing here invents a course, price, credential or partner.
 */
export interface ApplicationData {
  fullName: string;
  /** Contact email for this application; may differ from the account email. */
  contactEmail: string;
  audienceType: AudienceType;
  serviceInterest: ServiceInterest;
  deliveryPreference: DeliveryPreference;
  organization: string | null;
  /** Free-text: what the person or organisation is facing. */
  message: string;
  /** Explicit, never pre-checked. Recorded with the timestamp of agreement. */
  consentToContact: boolean;
  consentToPrivacyPolicy: boolean;
  /** Locale the form was completed in, for correct follow-up language. */
  locale: LanguageTag;
}

export interface Submission {
  readonly id: SubmissionId;
  readonly ownerId: AppUserId;
  /** Denormalised so storage rules and admin queries do not need a join. */
  readonly ownerAuthProviderUserId: AuthProviderUserId;
  readonly ownerEmail: string;
  readonly status: SubmissionStatus;
  readonly data: ApplicationData;
  readonly videoIds: readonly VideoId[];
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly submittedAt: Date | null;
  readonly consentRecordedAt: Date | null;
  /** Client-supplied idempotency key for the create call. */
  readonly clientRequestId: string | null;
  readonly statusUpdatedAt: Date | null;
  readonly statusUpdatedByAppUserId: AppUserId | null;
}

/**
 * Administrator-only material. Stored in a separate record so that a bug in
 * the owner-facing read path can never leak internal notes.
 */
export interface SubmissionInternal {
  readonly submissionId: SubmissionId;
  readonly notes: readonly SubmissionNote[];
  readonly updatedAt: Date;
}

export interface SubmissionNote {
  readonly id: string;
  readonly authorAppUserId: AppUserId;
  readonly authorEmail: string;
  readonly body: string;
  readonly createdAt: Date;
}

/** Shape returned to the submission owner. Never contains internal notes. */
export interface SubmissionView {
  id: SubmissionId;
  status: SubmissionStatus;
  data: ApplicationData;
  videoIds: VideoId[];
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  canEdit: boolean;
  canDelete: boolean;
}

export const isEditableByOwner = (submission: Submission): boolean =>
  submission.status === "draft";

export const isDeletableByOwner = (submission: Submission): boolean =>
  submission.status === "draft" || submission.status === "withdrawn";

export const toSubmissionView = (submission: Submission): SubmissionView => ({
  id: submission.id,
  status: submission.status,
  data: submission.data,
  videoIds: [...submission.videoIds],
  createdAt: submission.createdAt.toISOString(),
  updatedAt: submission.updatedAt.toISOString(),
  submittedAt: submission.submittedAt
    ? submission.submittedAt.toISOString()
    : null,
  canEdit: isEditableByOwner(submission),
  canDelete: isDeletableByOwner(submission),
});

/** Shape returned to administrators. Includes owner identity and notes. */
export interface AdminSubmissionView extends SubmissionView {
  ownerId: AppUserId;
  ownerEmail: string;
  statusUpdatedAt: string | null;
  notes: Array<{
    id: string;
    authorEmail: string;
    body: string;
    createdAt: string;
  }>;
}
